import React, { useEffect, useState, Fragment } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Paper, Grid, Typography, TextField, MenuItem, Zoom, CircularProgress } from '@material-ui/core/';
import { authFetch, logout } from '../../auth';
import Plot from 'react-plotly.js';
import * as localeDictionary from 'plotly.js/lib/locales/es.js';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box'
    },
    sectionTitle: {
        marginBottom: 15,
        backgroundColor: theme.palette.secondary.light,
        borderRadius: 4,
        padding: theme.spacing(1.5),
        fontWeight: 'bold'
    },
    contentPaper: {
        background: theme.palette.secondary.light,
        padding: 8,
    },
    formRow: {
        width: "100%",
    },
    formText: {
        fontSize: 14,
        color: theme.palette.primary.main,
        textAlign: "center",
    },
    itemText: {
        fontSize: 14,
        color: theme.palette.primary.main,
    },
    // Contenedor elástico para obligar a Plotly a respetar las dimensiones del padre
    plotWrapper: {
        width: '100%',
        height: '58vh',
        marginTop: theme.spacing(2),
        overflow: 'hidden'
    },
    plotContainer: {
        padding: theme.spacing(2),
        width: '100%',
        height: '100%'
    }
}));

const mesesCortos = {
    '01': 'Ene', '02': 'Feb', '03': 'Mar', '04': 'Abr', 
    '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Ago', 
    '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic'
};

// Función auxiliar para transformar "2026-02" en "Feb 2026"
const formatPeriodoTexto = (periodoStr) => {
    const [año, mes] = periodoStr.split('-');
    return `${mesesCortos[mes] || mes} ${año}`;
};

export default function TariffControl(props) {
    const classes = useStyles();
    const theme = useTheme();

    const [isLoading, setIsLoading] = useState(false);
    const [trendLoading, setTrendLoading] = useState(false);
    const [yearInformation, setYearInformation] = useState([]);
    
    // Controles de tiempo optimizados
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedRange, setSelectedRange] = useState('com'); // 'com', 'tri', 'sem', 'his'
    const [selectedPeriod, setSelectedPeriod] = useState('');

    // Parámetros CREG
    const [level, setLevel] = useState(1);
    const [property, setProperty] = useState('or');
    const [plotData, setPlotData] = useState([]);

    let trimCmp = ['1er Trimestre', '2do Trimestre', '3er Trimestre', '4to Trimestre'];
    let semCmp = ['1er Semestre', '2do Semestre'];

    // Carga inicial de límites de años de la aplicación
    useEffect(() => {
        setIsLoading(true);
        document.title = "Analítica de Tarifas | Dashboard Profesional";
        
        authFetch("/api/get-liquidation-date-limits?username=" + String(props.username))
            .then(res => res.status === 422 ? logout() : res.json())
            .then(response => {
                if (response && response.years_information) {
                    setYearInformation(response.years_information);
                } else {
                    setYearInformation([{ year: 2024 }, { year: 2025 }, { year: 2026 }]);
                }
                setIsLoading(false);
            }).catch(() => setIsLoading(false));
    }, [props.username]);

    // Consumo de datos adaptado a la nueva estructura de control temporal
    useEffect(() => {
        // Guardas de validación
        if (selectedRange !== 'com' && selectedRange !== 'his' && !selectedPeriod) return;

        setTrendLoading(true);
        let url = `/api/get-electric-tariffs-trends?username=${props.username}&range=${selectedRange}&level=${level}&property=${property}`;
        
        // Solo adjuntamos año si no estamos en modo histórico total
        if (selectedRange !== 'his') {
            url += `&year=${selectedYear}`;
        }
        if (selectedPeriod) {
            url += `&period=${selectedPeriod}`;
        }

        authFetch(url)
            .then(res => res.status === 422 ? logout() : res.json())
            .then(response => {
                if (response && response.tariff_trends) {
                    buildProfessionalGraph(response.tariff_trends);
                }
                setTrendLoading(false);
            }).catch(() => setTrendLoading(false));

    }, [selectedYear, selectedRange, selectedPeriod, level, property, props.username]);

    const buildProfessionalGraph = (trends) => {
        const xData = trends.map(t => t.Periodo);
        // Creamos un arreglo de strings amigables: ["Ene 2026", "Feb 2026", ...]
        const fechasFormateadas = trends.map(t => formatPeriodoTexto(t.Periodo));

        // 1. Barra de fondo (CU Monomio) con Fecha Detallada
        const barTrace = {
            x: xData,
            y: trends.map(t => t.CU_Monomio),
            customdata: fechasFormateadas, // Pasamos las fechas formateadas aquí
            type: 'bar',
            name: 'CU Monomio',
            marker: {
                color: 'rgba(99, 102, 241, 0.15)',
                line: { color: 'rgba(99, 102, 241, 0.4)', width: 1.5 }
            },
            // %{customdata} renderiza el Mes y Año asignado a ese punto exacto
            hovertemplate: '<b>CU General Monomio</b><br>Periodo: %{customdata}<br>Valor: %{y:.2f} $/kWh<extra></extra>'
        };

        // Paleta de Colores Moderna (Tech Dashboard)
        const components = [
            { key: 'Generación', name: 'G - Generación', color: '#06B6D4' },
            { key: 'Transmisión', name: 'T - Transmisión', color: '#10B981' },
            { key: 'Distribución', name: 'D - Distribución', color: '#6366F1' },
            { key: 'Comercialización', name: 'C - Comercialización', color: '#F59E0B' },
            { key: 'Restricciones', name: 'R - Restricciones', color: '#EF4444' },
            { key: 'Pérdidas', name: 'P - Pérdidas', color: '#8B5CF6' },
        ];

        // 2. Líneas de tendencia con Fecha Detallada por punto
        const lineTraces = components.map(comp => ({
            x: xData,
            y: trends.map(t => t[comp.key]),
            customdata: fechasFormateadas, // Pasamos las fechas formateadas aquí
            type: 'scatter',
            mode: 'lines+markers',
            name: comp.name,
            line: { shape: 'spline', width: 3, color: comp.color },
            marker: { size: 6, symbol: 'circle' },
            hovertemplate: `<b>${comp.name}</b><br>Periodo: %{customdata}<br>Costo: %{y:.2f} $/kWh<extra></extra>`
        }));

        setPlotData([barTrace, ...lineTraces]);
    };

    // Configuración del Layout Profesional Ajustable
    const layout = {
        autosize: true, // Crucial para responder al contenedor elástico
        xaxis: { 
            title: 'Periodo de Facturación', 
            tickfont: { size: 11, family: 'Asap Condensed' }, 
            gridcolor: 'rgba(0,0,0,0.04)',
            fixedrange: true 
        },
        yaxis: { 
            title: 'Costo ($ / kWh)', 
            tickfont: { size: 11, family: 'Asap Condensed' }, 
            gridcolor: 'rgba(0,0,0,0.06)',
            zerolinecolor: 'rgba(0,0,0,0.1)',
            fixedrange: true 
        },
        barmode: 'overlay',
        bargap: 0.55, // HACE LAS BARRAS NOTABLEMENTE MÁS DELGADAS Y LIMPIAS
        hovermode: "closest",
        legend: { 
            orientation: "h", 
            y: 1.12, 
            x: 0,
            font: { size: 12, family: 'Asap Condensed' }
        },
        font: { family: 'Asap Condensed', color: theme.palette.primary.dark },
        paper_bgcolor: theme.palette.background.default,
        plot_bgcolor: theme.palette.background.default,
        margin: { b: 40, t: 10, l: 50, r: 10 }
    };

    return (
        <div className={classes.root}>
            <Zoom in timeout={800}>
                <Grid container justify="space-evenly" spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.sectionTitle} color="primary">
                            Tendencias de Componentes Tarifarios CU (CREG)
                        </Typography>
                    </Grid>

                    {isLoading ? <CircularProgress /> : (
                        <Fragment>
                            {/* SECCIÓN DE CONTROLES */}
                            <Grid item xs={12}>
                                <Grid container spacing={1} alignItems="center" justify="flex-start">
                                    
                                    {/* Selector de Rango Temporal */}
                                    <Grid item xs={3}>
                                        <Paper className={classes.contentPaper} elevation={0} variant="outlined">
                                            <TextField
                                                variant="standard" select label="Rango de Consulta" size="small"
                                                className={classes.formRow} value={selectedRange}
                                                InputProps={{ classes: { input: classes.formText } }}
                                                onChange={(e) => { 
                                                    setSelectedRange(e.target.value); 
                                                    setSelectedPeriod(''); 
                                                    setPlotData([]); 
                                                }}
                                            >
                                                <MenuItem value="tri" className={classes.itemText}>Trimestral</MenuItem>
                                                <MenuItem value="sem" className={classes.itemText}>Semestral</MenuItem>
                                                <MenuItem value="com" className={classes.itemText}>Anual (Año Completo)</MenuItem>
                                                <MenuItem value="his" className={classes.itemText}><b>Histórico Total</b></MenuItem>
                                            </TextField>
                                        </Paper>
                                    </Grid>

                                    {/* Selector de Año - Inhabilitado si es Histórico */}
                                    <Grid item xs={2}>
                                        <Paper className={classes.contentPaper} elevation={0} variant="outlined" style={{ opacity: selectedRange === 'his' ? 0.5 : 1 }}>
                                            <TextField
                                                variant="standard" select label="Año" size="small"
                                                className={classes.formRow} value={selectedYear}
                                                disabled={selectedRange === 'his'}
                                                InputProps={{ classes: { input: classes.formText } }}
                                                onChange={(e) => { setSelectedYear(e.target.value); setPlotData([]); }}
                                            >
                                                {yearInformation.map((info, idx) => (
                                                    <MenuItem key={idx} value={info.year} className={classes.itemText}>
                                                        {info.year}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Paper>
                                    </Grid>

                                    {/* Selector de Periodo Detallado */}
                                    {['tri', 'sem'].includes(selectedRange) && (
                                        <Grid item xs={2}>
                                            <Paper className={classes.contentPaper} elevation={0} variant="outlined">
                                                <TextField
                                                    variant="standard" select label="Periodo" size="small"
                                                    className={classes.formRow} value={selectedPeriod}
                                                    InputProps={{ classes: { input: classes.formText } }}
                                                    onChange={(e) => setSelectedPeriod(e.target.value)}
                                                >
                                                    {selectedRange === 'tri' ? 
                                                        trimCmp.map((trim, tidx) => <MenuItem key={tidx} value={"t" + (tidx + 1)} className={classes.itemText}>{trim}</MenuItem>) :
                                                        semCmp.map((sem, sidx) => <MenuItem key={sidx} value={"s" + (sidx + 1)} className={classes.itemText}>{sem}</MenuItem>)
                                                    }
                                                </TextField>
                                            </Paper>
                                        </Grid>
                                    )}

                                    {/* Selector Nivel de Tensión */}
                                    <Grid item xs={2}>
                                        <Paper className={classes.contentPaper} elevation={0} variant="outlined">
                                            <TextField
                                                variant="standard" select label="Nivel Tensión" size="small"
                                                className={classes.formRow} value={level}
                                                InputProps={{ classes: { input: classes.formText } }}
                                                onChange={(e) => { 
                                                    const lvl = e.target.value;
                                                    setLevel(lvl);
                                                    if(lvl !== 1) setProperty('or');
                                                }}
                                            >
                                                <MenuItem value={1} className={classes.itemText}>Nivel 1</MenuItem>
                                                <MenuItem value={2} className={classes.itemText}>Nivel 2</MenuItem>
                                                <MenuItem value={3} className={classes.itemText}>Nivel 3</MenuItem>
                                            </TextField>
                                        </Paper>
                                    </Grid>

                                    {/* Selector Propiedad de Activos */}
                                    <Grid item xs={2}>
                                        <Paper className={classes.contentPaper} elevation={0} variant="outlined" style={{ opacity: level !== 1 ? 0.5 : 1 }}>
                                            <TextField
                                                variant="standard" select label="Propiedad Activos" size="small"
                                                className={classes.formRow} value={property}
                                                disabled={level !== 1}
                                                InputProps={{ classes: { input: classes.formText } }}
                                                onChange={(e) => setProperty(e.target.value)}
                                            >
                                                <MenuItem value="or" className={classes.itemText}>Propiedad OR</MenuItem>
                                                <MenuItem value="comp" className={classes.itemText}>OR Compartido</MenuItem>
                                                <MenuItem value="us" className={classes.itemText}>Usuario</MenuItem>
                                            </TextField>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* SECCIÓN FLEXIBLE DEL GRÁFICO */}
                            <Grid item xs={12} className={classes.plotWrapper}>
                                {trendLoading ? (
                                    <Grid container justify="center"><CircularProgress style={{ marginTop: 40 }} /></Grid>
                                ) : plotData.length > 0 ? (
                                    <Paper className={classes.plotContainer} elevation={0}>
                                        <Plot
                                            data={plotData}
                                            layout={layout}
                                            useResizeHandler={true} // Obliga al plot a redimensionarse dinámicamente
                                            style={{ width: '100%', height: '100%' }}
                                            config={{ displayModeBar: false, locales: { 'es-ES': localeDictionary }, locale: 'es-ES' }}
                                        />
                                    </Paper>
                                ) : (
                                    <Typography align="center" style={{ marginTop: 40, color: '#999', fontSize: 14 }}>
                                        Selecciona los rangos de consulta tarifaria para renderizar los acumulados.
                                    </Typography>
                                )}
                            </Grid>
                        </Fragment>
                    )}
                </Grid>
            </Zoom>
        </div>
    );
}   